/**
 * App initialization hook - orchestrates loading of all required data
 *
 * Loads data in parallel after auth:
 * 1. Authentication state (wait for onAuthStateChanged)
 * 2. Card database index (CDN + IndexedDB cache) - for search
 * 3. If authenticated: sync hooks load collection, folders, decks, deck cards, settings in parallel
 * 4. Card cache populator fetches Scryfall data for collection AND deck cards (after both sync)
 *
 * User data syncs no longer wait for card database - they're independent.
 * The enrichedCollectionAtom is a derived atom that combines collection + cache.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';

import { onAuthStateChanged, type User } from 'firebase/auth';

import type { LoadingTask, LoadingTaskStatus } from '@/components/AppLoader/AppLoader';
import { useCardCachePopulator, useCollectionSync } from '@/features/collection/hooks/api';
import { useDeckCardsSync, useDecksSync } from '@/features/decks/hooks/api';
import { useFoldersSync } from '@/features/folders/hooks/api';
import { useListCardsSync, useListsSync } from '@/features/lists/hooks/api';
import { useSetsSync } from '@/features/sets/hooks/api';
import { useSettingsSync } from '@/features/settings/hooks/api';
import { useStarredSync } from '@/features/starred/hooks/api';
import { useCardIndexLoader } from '@/hooks/useCardIndex';
import { auth } from '@/services/firebase';

// ============================================================================
// Types
// ============================================================================

interface InitializationState {
  isReady: boolean;
  error: string | null;
  tasks: LoadingTask[];
}

type TaskId =
  'cards' | 'auth' | 'collection' | 'folders' | 'settings' | 'enrichment' | 'sets' | 'decks' | 'deckCards' | 'starred' | 'lists';

interface TaskState {
  status: LoadingTaskStatus;
  error?: string;
}

// ============================================================================
// Hook
// ============================================================================

export default function useAppInitialization(): InitializationState & { retry: () => void } {
  // Card database loading (existing hook)
  const { loading: cardsLoading } = useCardIndexLoader();

  // Auth state
  const [authState, setAuthState] = useState<{ resolved: boolean; user: User | null }>({
    resolved: false,
    user: null,
  });

  // Overall error state
  const [globalError, setGlobalError] = useState<string | null>(null);

  // Retry counter to force re-initialization
  const [retryCount, setRetryCount] = useState(0);

  // ============================================================================
  // Auth State Listener
  // ============================================================================
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(
      auth,
      (user) => {
        setAuthState({ resolved: true, user });
      },
      (error) => {
        console.error('Auth state error:', error);
        setGlobalError('Failed to check authentication status');
        setAuthState({ resolved: true, user: null });
      },
    );

    return unsubscribe;
  }, [retryCount]);

  // ============================================================================
  // Sync Hooks (enabled after auth resolves with a user - no card DB wait!)
  // ============================================================================
  const isAuthenticated = authState.resolved && authState.user !== null;

  const isLoadingFolders = useFoldersSync(isAuthenticated);
  const isLoadingCollection = useCollectionSync(isAuthenticated);
  const isLoadingSettings = useSettingsSync(isAuthenticated);
  const isLoadingDecks = useDecksSync(isAuthenticated);
  const isLoadingStarred = useStarredSync(isAuthenticated);
  const isLoadingLists = useListsSync(isAuthenticated);
  const isLoadingListCards = useListCardsSync(isAuthenticated);

  // Deck cards sync (cards across all decks for cross-deck features)
  const isLoadingDeckCards = useDeckCardsSync(isAuthenticated);

  // Sets load in parallel with auth (no auth required, public data)
  const isLoadingSets = useSetsSync();

  // Cache populator fetches Scryfall data for collection AND deck cards
  // Only enabled after both syncs are complete
  const dataSyncDone = isAuthenticated && !isLoadingCollection && !isLoadingDeckCards;
  const isLoadingEnrichment = useCardCachePopulator(dataSyncDone);

  // ============================================================================
  // Build Task States
  // ============================================================================
  const taskStates = useMemo<Record<TaskId, TaskState>>(() => {
    // Auth loads first
    const authStatus: LoadingTaskStatus = !authState.resolved ? 'loading' : 'complete';

    // Cards load in parallel with auth
    const cardsStatus: LoadingTaskStatus = cardsLoading ? 'loading' : 'complete';

    // Sets load in parallel with auth (public data, no auth required)
    const setsStatus: LoadingTaskStatus = isLoadingSets ? 'loading' : 'complete';

    // User data tasks: pending until auth done, then loading, then complete
    // No longer waits for card database!
    const userDataPending = !isAuthenticated;

    const settingsStatus: LoadingTaskStatus = userDataPending ? 'pending' : isLoadingSettings ? 'loading' : 'complete';
    const foldersStatus: LoadingTaskStatus = userDataPending ? 'pending' : isLoadingFolders ? 'loading' : 'complete';
    const collectionStatus: LoadingTaskStatus = userDataPending ? 'pending' : isLoadingCollection ? 'loading' : 'complete';
    const decksStatus: LoadingTaskStatus = userDataPending ? 'pending' : isLoadingDecks ? 'loading' : 'complete';
    const deckCardsStatus: LoadingTaskStatus = userDataPending ? 'pending' : isLoadingDeckCards ? 'loading' : 'complete';
    const starredStatus: LoadingTaskStatus = userDataPending ? 'pending' : isLoadingStarred ? 'loading' : 'complete';
    const listsStatus: LoadingTaskStatus = userDataPending ? 'pending' : isLoadingLists || isLoadingListCards ? 'loading' : 'complete';

    // Enrichment: pending until both collection and deck cards done, then loading while fetching card data
    const enrichmentPending = userDataPending || isLoadingCollection || isLoadingDeckCards;
    const enrichmentStatus: LoadingTaskStatus = enrichmentPending ? 'pending' : isLoadingEnrichment ? 'loading' : 'complete';

    return {
      auth: { status: authStatus },
      cards: { status: cardsStatus },
      sets: { status: setsStatus },
      settings: { status: settingsStatus },
      folders: { status: foldersStatus },
      collection: { status: collectionStatus },
      decks: { status: decksStatus },
      deckCards: { status: deckCardsStatus },
      starred: { status: starredStatus },
      lists: { status: listsStatus },
      enrichment: { status: enrichmentStatus },
    };
  }, [
    cardsLoading,
    authState.resolved,
    isAuthenticated,
    isLoadingFolders,
    isLoadingCollection,
    isLoadingSettings,
    isLoadingDecks,
    isLoadingDeckCards,
    isLoadingStarred,
    isLoadingLists,
    isLoadingListCards,
    isLoadingEnrichment,
    isLoadingSets,
  ]);

  // ============================================================================
  // Build tasks array for UI
  // ============================================================================
  const tasks = useMemo<LoadingTask[]>(() => {
    // Always show all tasks - user data tasks show as pending until auth completes
    const allTasks: LoadingTask[] = [
      {
        id: 'auth',
        label: 'Authentication',
        status: taskStates.auth.status,
        error: taskStates.auth.error,
      },
      {
        id: 'cards',
        label: 'Card database',
        status: taskStates.cards.status,
        error: taskStates.cards.error,
      },
      {
        id: 'sets',
        label: 'Sets',
        status: taskStates.sets.status,
        error: taskStates.sets.error,
      },
      {
        id: 'settings',
        label: 'Settings',
        status: taskStates.settings.status,
        error: taskStates.settings.error,
      },
      {
        id: 'folders',
        label: 'Folders',
        status: taskStates.folders.status,
        error: taskStates.folders.error,
      },
      {
        id: 'collection',
        label: 'Collection',
        status: taskStates.collection.status,
        error: taskStates.collection.error,
      },
      {
        id: 'decks',
        label: 'Decks',
        status: taskStates.decks.status,
        error: taskStates.decks.error,
      },
      {
        id: 'deckCards',
        label: 'Deck cards',
        status: taskStates.deckCards.status,
        error: taskStates.deckCards.error,
      },
      {
        id: 'starred',
        label: 'Starred',
        status: taskStates.starred.status,
        error: taskStates.starred.error,
      },
      {
        id: 'lists',
        label: 'Lists',
        status: taskStates.lists.status,
        error: taskStates.lists.error,
      },
      {
        id: 'enrichment',
        label: 'Card data',
        status: taskStates.enrichment.status,
        error: taskStates.enrichment.error,
      },
    ];

    return allTasks;
  }, [taskStates]);

  // ============================================================================
  // Compute ready state
  // ============================================================================
  const isReady = useMemo(() => {
    // Auth, cards, and sets must be complete
    if (taskStates.auth.status !== 'complete') return false;
    if (taskStates.cards.status !== 'complete') return false;
    if (taskStates.sets.status !== 'complete') return false;

    // If authenticated, all user data must be loaded including enrichment
    if (isAuthenticated) {
      if (taskStates.settings.status !== 'complete') return false;
      if (taskStates.folders.status !== 'complete') return false;
      if (taskStates.collection.status !== 'complete') return false;
      if (taskStates.decks.status !== 'complete') return false;
      if (taskStates.deckCards.status !== 'complete') return false;
      if (taskStates.starred.status !== 'complete') return false;
      if (taskStates.lists.status !== 'complete') return false;
      if (taskStates.enrichment.status !== 'complete') return false;
    }

    return true;
  }, [taskStates, isAuthenticated]);

  // ============================================================================
  // Retry function
  // ============================================================================
  const retry = useCallback(() => {
    setGlobalError(null);
    setAuthState({ resolved: false, user: null });
    setRetryCount((c) => c + 1);
  }, []);

  return {
    isReady,
    error: globalError,
    tasks,
    retry,
  };
}
